import yaml from "js-yaml";
import xml2js from "xml2js";

export type StructuralFormat = "json" | "yaml" | "xml";

export interface CanonicalizeResult {
    text?: string;
    error?: string;
}

/**
 * Recursively sorts object keys (arrays keep their order - reordering an
 * array is a real change) so two documents that differ only in key order
 * produce identical output.
 */
function sortDeep(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map(sortDeep);
    }
    if (value !== null && typeof value === "object" && !(value instanceof Date)) {
        const sorted: Record<string, unknown> = {};
        Object.keys(value as Record<string, unknown>)
            .sort()
            .forEach((key) => {
                sorted[key] = sortDeep((value as Record<string, unknown>)[key]);
            });
        return sorted;
    }
    return value;
}

/**
 * Parses text in the given format, recursively sorts its keys, and
 * re-serializes it back to that same format. Diffing the canonical output
 * of two documents (with the existing line/word diff viewer) then ignores
 * formatting, whitespace, and key order - only real content changes show up.
 */
export async function canonicalizeForDiff(text: string, format: StructuralFormat): Promise<CanonicalizeResult> {
    if (!text || !text.trim()) {
        return { text: "" };
    }
    try {
        if (format === "json") {
            return { text: JSON.stringify(sortDeep(JSON.parse(text)), null, 2) };
        }
        if (format === "yaml") {
            return { text: yaml.dump(yaml.load(text), { sortKeys: true, lineWidth: -1 }) };
        }
        // xml
        const parsed = await xml2js.parseStringPromise(text);
        const builder = new xml2js.Builder({ renderOpts: { pretty: true, indent: "  " } });
        return { text: builder.buildObject(sortDeep(parsed)) };
    } catch (err) {
        return { error: err instanceof Error ? err.message : "Failed to parse input" };
    }
}
